'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { revalidatePath } from 'next/cache';
import { useRouter } from 'next/navigation';
import { convertToEnglishDigits } from '@/lib/actions';
import { persianToEnglishNumbers } from '@/utils/persianToEnglishNumbers';

/* ■■■■■■■■■■■■■■■■■■■■■■■■ Comp ■■■■■■■■■■■■■■■■■■■■■■■■ */
const AuthModal: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '']);
  const [step, setStep] = useState('phone');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(120);
  const [isCodeExpired, setIsCodeExpired] = useState(false);

  const [phoneError, setPhoneError] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const firstDigitRef = useRef<HTMLInputElement>(null); // Ref for first input field
  const [loading, setLoading] = useState(false);

  const router = useRouter();

  /* Effect --------------------------------------------- */
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (step === 'verify') {
      // Set focus on the first input only when `step` becomes 'verify'
      if (firstDigitRef.current) {
        firstDigitRef.current.focus();
      }

      if (countdown > 0) {
        timer = setInterval(() => {
          setCountdown((prevCount) => {
            if (prevCount === 1) {
              clearInterval(timer); // Clear the timer when countdown reaches 0
              setIsCodeExpired(true); // Set code expired when countdown hits 0
              return 0; // Return 0 to stop countdown
            }
            return prevCount - 1; // Continue counting down
          });
        }, 1000);
      }
    }

    // Cleanup the interval when component unmounts or `step` changes
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [step]); // Only depend on `step` to set focus and start countdown

  /* Send Token Code To User ---------------------------- */
  const handleSendCode = async () => {
    if (!validatePhoneNumber()) return;
    setLoading(true); // Start loading

    try {
      const response = await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber, template: 'verify' }),
      });

      if (!response.ok) throw new Error('Failed to send code');
      setStep('verify');
      setCountdown(120);
      setIsCodeExpired(false);
      setVerificationCode(['', '', '', '']); // Reset the 4-digit code input
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  /* HandleVerifyCode ----------------------------------- */
  const handleVerifyCode = async (
    phoneNumber: string,
    verificationCode: string
  ) => {
    // Convert Persian numbers to English
    phoneNumber = await convertToEnglishDigits(phoneNumber);
    verificationCode = await convertToEnglishDigits(verificationCode);
    // Validate phone number format (starts with 091 and has 11 digits)
    const phoneRegex = /^09\d{9}$/;
    const tokenRegex = /^\d{4}$/; // Token should be 4 digits

    if (!phoneRegex.test(phoneNumber)) {
      return alert('Invalid phone number format.');
    }
    if (!tokenRegex.test(verificationCode)) {
      return alert('Invalid token format.');
    }
    setLoading(true); // Start loading

    try {
      const response = await axios.post('/api/verify-token', {
        phoneNumber,
        token: verificationCode, // Ensure 'token' is sent as the key
      });

      if (response.data.success) {
        if (response.data.hasPassword) {
          router.refresh();
        } else {
          // If user does not have a password, take appropriate action
          setStep('addPass');
        }
      } else {
        alert(response.data.message); // Show backend error message
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false); // Stop loading
    }
  };
  /* HandleSubmit --------------------------------------- */
  const handleSubmit = async (phoneNumber: string, password: string) => {
    phoneNumber = await convertToEnglishDigits(phoneNumber);
    password = await convertToEnglishDigits(password);

    // Validate phone number format (starts with 091 and has 11 digits)
    const phoneRegex = /^091\d{8}$/;

    if (!phoneRegex.test(phoneNumber)) {
      return alert('Invalid phone number format.');
    }
    setLoading(true);
    try {
      const response = await axios.post('/api/submit-auth-form', {
        phoneNumber,
        password, // Ensure 'token' is sent as the key
      });

      if (response.data.success) {
        router.refresh();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Wrong password or number:', error);
      router.refresh();

      alert('Something went wrong. Please try again.');
    } finally {
      router.refresh();

      setLoading(false);
    }
  };

  /* HandlePass ----------------------------------------- */

  const handlePass = async (phoneNumber: string, password: string) => {
    try {
      const response = await axios.post('/api/ad-pss', {
        phoneNumber,
        password, // Ensure 'token' is sent as the key
      });

      if (response.data.success) {
        router.refresh();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Wrong password or number:', error);
      router.refresh();

      alert('Something went wrong. Please try again.');
    }
  };

  const handleCodeInput = (index: number, value: string) => {
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Move focus to the next input if the value is not empty and if it's not the last input
    if (value && index < 3) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }
    }

    // If the value is empty, move focus to the previous input
    if (!value && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) {
        (prevInput as HTMLInputElement).focus();
      }
    }
  };

  const validatePhoneNumber = () => {
    const phoneRegex = /^09\d{9}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setPhoneError(true);
      setError('شماره موبایل نامعتبر است');
      return false;
    }
    setPhoneError(false);
    return true;
  };

  const validateVerificationCode = () => {
    if (verificationCode.join('').length !== 4) {
      setCodeError(true);
      setError('کد تایید نامعتبر است');
      return false;
    }
    setCodeError(false);
    return true;
  };

  const validatePassword = () => {
    if (password.length < 6) {
      setPasswordError(true);
      setError('رمز عبور باید حداقل ۶ کاراکتر باشد');
      return false;
    }
    setPasswordError(false);
    return true;
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    e.target.value = persianToEnglishNumbers(value);
    setPhoneNumber(e.target.value);
  };

  /* ■■■■■■■■■■■■■■■■■■■■■■ Return ■■■■■■■■■■■■■■■■■■■■■■ */

  return (
    <Dialog>
      <DialogTrigger
        className="sm:mx-4 mx-2"
        asChild
      >
        <Button variant="outline">ورود یا ثبت نام</Button>
      </DialogTrigger>
      <DialogContent className="rtl:text-right sm:max-w-[425px]">
        {/* <DialogHeader>
          <DialogTitle className="text-center text-2xl font-semibold">
            ورود یا ثبت‌ نام
          </DialogTitle>
          <DialogDescription className="text-center pt-4 text-lg">
            .برای ادامه شماره موبایل خود را وارد کنید
          </DialogDescription>
        </DialogHeader> */}

        <div className="grid gap-4 py-4">
          {step === 'phone' && (
            <div className="flex flex-col items-center gap-4">
              <h1 className="text-center text-2xl font-semibold">
                ورود یا ثبت‌ نام
              </h1>
              <p className="text-center">
                .برای ادامه شماره موبایل خود را وارد کنید
              </p>
              <Input
                id="phone"
                type="tel"
                placeholder="۰۹۱۲۳۴۵۲۱۴۹"
                value={phoneNumber}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent the default form submission behavior
                    handleSendCode(); // Trigger the code send action
                  }
                }}
                className={`col-span-3 rtl:text-right ${
                  phoneError ? 'border-red-500' : ''
                }`}
              />
            </div>
          )}
          {step === 'verify' && (
            <>
              <Label className="text-right">کد تایید</Label>
              <div className="flex justify-center gap-2">
                {verificationCode.map((digit, index) => (
                  <Input
                    ref={index === 0 ? firstDigitRef : null} // Set ref for the first input
                    key={index}
                    id={`code-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault(); // Prevent the default form submission behavior
                        handleVerifyCode(
                          phoneNumber,
                          verificationCode.join('')
                        ); // Trigger the code send action
                      }
                    }}
                    onChange={(e) => handleCodeInput(index, e.target.value)}
                    className={`w-12 h-12 text-center ${
                      codeError ? 'border-red-500' : ''
                    }`}
                  />
                ))}
              </div>
              {!isCodeExpired && (
                <p className="text-center mt-2">
                  کد تایید تا {Math.floor(countdown / 60)}:
                  {countdown % 60 < 10 ? '0' : ''}
                  {countdown % 60} دیگر معتبر است
                </p>
              )}
              {isCodeExpired && (
                <Button
                  onClick={handleSendCode}
                  className="mt-2"
                >
                  ارسال مجدد کد
                </Button>
              )}
            </>
          )}
          {step === 'password' && (
            <div
              dir="rtl"
              className="grid grid-cols-4 items-center gap-4"
            >
              <Label
                htmlFor="phone"
                className="text-right"
              >
                شماره موبایل
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="۰۹۱۲۳۴۵۲۱۴۹"
                value={phoneNumber}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent the default form submission behavior
                    handleSendCode(); // Trigger the code send action
                  }
                }}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className={`col-span-3 rtl:text-right ${
                  passwordError ? 'border-red-500' : ''
                }`}
              />
              <Label
                htmlFor="password"
                className="text-right"
              >
                رمز عبور
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="رمز عبور خود را وارد کنید"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`col-span-3 rtl:text-right ${
                  passwordError ? 'border-red-500' : ''
                }`}
              />
            </div>
          )}
          {step === 'addPass' && (
            <div className="flex flex-col items-center gap-4">
              <h1 className="text-center text-2xl font-semibold">
                ساخت رمز عبور
              </h1>
              <p className="text-center">رمز عبور خود را وارد کنید </p>
              <Input
                dir="rtl"
                id="password"
                type="password"
                placeholder="رمز عبور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`col-span-3 rtl:text-right ${
                  phoneError ? 'border-red-500' : ''
                }`}
              />
            </div>
          )}
        </div>
        {error && <p className="text-red-500 mb-2 text-center">{error}</p>}

        {/* /* Footer --------------------------------------- */}
        <DialogFooter>
          {step === 'phone' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep('password')}
              >
                ورود با رمز
              </Button>
              <Button
                onClick={handleSendCode}
                disabled={!phoneNumber}
              >
                {loading ? (
                  <div className="spinner"></div>
                ) : (
                  'تایید و دریافت کد'
                )}
              </Button>
            </>
          )}
          {step === 'verify' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep('phone')}
              >
                اصلاح شماره
              </Button>
              <Button
                onClick={() =>
                  handleVerifyCode(phoneNumber, verificationCode.join(''))
                }
                disabled={
                  verificationCode.join('').length !== 4 || isCodeExpired
                }
              >
                {loading ? <div className="spinner"></div> : 'تایید کد'}{' '}
              </Button>
            </>
          )}
          {step === 'password' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep('phone')}
              >
                ورود با رمز یکبار مصرف
              </Button>
              <Button
                onClick={() => handleSubmit(phoneNumber, password)}
                disabled={!password}
              >
                {loading ? <div className="spinner"></div> : 'ورود'}
              </Button>
            </>
          )}
          {step === 'addPass' && (
            <>
              <Button
                onClick={() => handlePass(phoneNumber, password)}
                disabled={!password}
              >
                ثبت رمز
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
